import React from "react";
import ConfirmationModal from "./ConfirmationModal.jsx";
import { ORDER_DUPLICATE_EVENT } from "../utils/orderDuplicateConfirmation.js";

const value = (item) => item == null || String(item).trim() === "" ? "Non renseigné" : String(item);
const dateLabel = (item) => /^\d{4}-\d{2}-\d{2}$/.test(item || "") ? item.split("-").reverse().join("/") : value(item);

function OrderComparison({ group, index, isImport }) {
  const reference = group.orders[0];
  return (
    <section className="rounded-[4px] border border-slate-300 bg-white p-3" aria-label={`Doublon potentiel ${index + 1}`}>
      <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-slate-700">
        <span className="font-semibold">{dateLabel(reference.delivery_date)}</span>
        <span>{value(reference.address)}</span>
      </div>
      <dl className="mb-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[12px] sm:grid-cols-4">
        {[["Produit", reference.grain_type], ["Code produit", reference.grain_code], ["Silo", reference.loading_silo], ["Bâtiment", reference.building]].map(([label, text]) => (
          <div key={label} className="min-w-0"><dt className="text-slate-500">{label}</dt><dd className="break-words font-semibold text-slate-900">{value(text)}</dd></div>
        ))}
      </dl>
      <ul className="grid gap-1.5">
        {group.orders.map((order, orderIndex) => (
          <li key={`${order.order_id}:${orderIndex}`} className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1 rounded-[4px] border border-slate-200 bg-slate-50 px-2 py-1.5 text-[12px]">
            <div className="min-w-0 flex-1">
              <span className="font-semibold text-slate-900">{order.order_id || `Nouvelle commande ${order.submission_index}`}</span>
              <span className="ml-2 text-slate-600">{order.origin === "existing" ? "Déjà enregistrée" : isImport ? "Dans le fichier" : "Saisie à enregistrer"}</span>
              <p className="break-words text-slate-700">{value(order.client_name)}</p>
            </div>
            <span className="whitespace-nowrap font-semibold text-slate-900">{Number(order.quantity_tons).toLocaleString("fr-CA", { maximumFractionDigits: 3 })} t</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function OrderDuplicateConfirmationDialog() {
  const [pending, setPending] = React.useState(null);
  const pendingRef = React.useRef(null);
  React.useEffect(() => {
    const handle = (event) => {
      if (event.detail.handled) return;
      event.detail.handled = true;
      pendingRef.current?.resolve(false);
      pendingRef.current = event.detail;
      setPending(event.detail);
    };
    window.addEventListener(ORDER_DUPLICATE_EVENT, handle);
    return () => {
      window.removeEventListener(ORDER_DUPLICATE_EVENT, handle);
      pendingRef.current?.resolve(false);
      pendingRef.current = null;
    };
  }, []);

  if (!pending) return null;
  const finish = (accepted) => {
    const current = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    current?.resolve(accepted);
  };
  const challenge = pending.challenge;
  const isImport = challenge.operation === "import";
  return (
    <ConfirmationModal
      title="Doublons potentiels à vérifier"
      eyebrow={isImport ? "Import en attente" : "Commande en attente"}
      description={isImport
        ? `Les ${challenge.submitted_count} commande(s) du fichier ont été analysées. Aucune commande du fichier n’a été enregistrée.`
        : "Aucune modification n’a été enregistrée. Vérifiez s’il s’agit d’une deuxième livraison volontaire."}
      warningMessage="Même adresse, même date, même produit, même code produit, même silo et même bâtiment. Les quantités peuvent être différentes."
      confirmLabel={isImport ? "Importer tout, doublons compris" : "Confirmer malgré le doublon"}
      cancelLabel="Revenir corriger"
      onConfirm={() => finish(true)}
      onCancel={() => finish(false)}
      onClose={() => finish(false)}
      cornerRadius="grain"
      contentDensity="compact"
      tone="warning"
    >
      <div className="mt-3 grid gap-2" data-order-duplicate-comparisons="true">
        {challenge.groups.map((group, index) => <OrderComparison key={index} group={group} index={index} isImport={isImport} />)}
      </div>
    </ConfirmationModal>
  );
}
